/**
 * Value Entry Task Component
 * 
 * Allows a participant to provide values for an attribute across all items.
 * Supports multiple value formats: number, fibonacci, t-shirt, scales, custom.
 * 
 * Features:
 * - Configure value format before entry (if not already configured)
 * - Enter values for each item
 * - Optional notes per item
 * - Submit all values at once
 */

import { useState, useMemo } from 'react';
import { recordValueRealization } from '../../../lib/valueOutcomesService';
import { saveSessionValue } from '../../../lib/sessionValueEntry';
import { finishEstimateAsk } from '../../../lib/askForEstimate';
import {
  Save,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  FileEdit,
  Hash,
  Shirt,
  List,
} from 'lucide-react';
import type {
  PrioritizationTask,
  ValueEntryConfig,
  ValueFormat,
  ValueEntryResponse,
} from '../../../types/prioritization/prioritizationTask';
import { usePrioritizationTaskStore } from '../../../stores/prioritization/prioritizationTaskStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';

// ============================================================================
// Types
// ============================================================================

export interface ValueEntryTaskProps {
  task: PrioritizationTask;
  userId: string;
  userName: string;
  onComplete?: () => void;
}

// ============================================================================
// Value Format Options
// ============================================================================

const VALUE_FORMATS: Array<{ id: ValueFormat; label: string; description: string; icon: typeof Hash }> = [
  { id: 'number', label: 'Number', description: 'Plain numeric value (e.g., 1-100)', icon: Hash },
  { id: 'fibonacci', label: 'Fibonacci', description: 'Fibonacci sequence (1, 2, 3, 5, 8, 13, 21...)', icon: Hash },
  { id: 'tshirt', label: 'T-Shirt Sizes', description: 'XS, S, M, L, XL, XXL', icon: Shirt },
  { id: 'scale_1_5', label: '1-5 Scale', description: 'Simple 5-point scale', icon: List },
  { id: 'scale_1_10', label: '1-10 Scale', description: '10-point scale for more precision', icon: List },
];

const FIBONACCI_VALUES = [1, 2, 3, 5, 8, 13, 21, 34, 55, 89];
const TSHIRT_VALUES = [
  { value: 'XS', label: 'XS' },
  { value: 'S', label: 'S' },
  { value: 'M', label: 'M' },
  { value: 'L', label: 'L' },
  { value: 'XL', label: 'XL' },
  { value: 'XXL', label: 'XXL' },
];

// ============================================================================
// Value Input Component
// ============================================================================

interface ValueInputProps {
  format: ValueFormat;
  value: string | number | undefined;
  onChange: (value: string | number) => void;
  config?: ValueEntryConfig;
}

function ValueInput({ format, value, onChange, config }: ValueInputProps) {
  switch (format) {
    case 'number':
      return (
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
          min={config?.min}
          max={config?.max}
          className="w-24 px-3 py-2 border text-center focus:ring-2 focus:ring-primary-500"
          placeholder="0"
        />
      );
      
    case 'fibonacci':
      return (
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
          className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-24 px-3 py-2 border focus:ring-2 focus:ring-primary-500"
        >
          <option value="">--</option>
          {FIBONACCI_VALUES.map(v => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      );
      
    case 'tshirt':
      return (
        <div className="flex gap-1">
          {TSHIRT_VALUES.map(({ value: v, label }) => (
            <button
              key={v}
              onClick={() => onChange(v)}
              className={`px-3 py-1.5 text-sm  border transition-colors ${
                value === v
                  ? 'bg-primary-100 border-primary-500 text-primary-700'
                  : 'bg-[var(--bg-raised)] hover:bg-[var(--surface)]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      );
      
    case 'scale_1_5':
      return (
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map(v => (
            <button
              key={v}
              onClick={() => onChange(v)}
              className={`w-10 h-10  border transition-colors ${
                value === v
                  ? 'bg-primary-100 border-primary-500 text-primary-700 font-medium'
                  : 'bg-[var(--bg-raised)] hover:bg-[var(--surface)]'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      );
      
    case 'scale_1_10':
      return (
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(v => (
            <button
              key={v}
              onClick={() => onChange(v)}
              className={`w-8 h-8 text-sm  border transition-colors ${
                value === v
                  ? 'bg-primary-100 border-primary-500 text-primary-700 font-medium'
                  : 'bg-[var(--bg-raised)] hover:bg-[var(--surface)]'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      );
      
    case 'custom':
      // A scale's named points (Low / Medium / High): one button each; the value
      // saved is the point's value, as on the item page.
      if (config?.options && config.options.length > 0) {
        return (
          <div className="flex flex-wrap gap-1">
            {config.options.map(({ value: v, label }) => (
              <button
                key={String(v)}
                type="button"
                onClick={() => onChange(v)}
                aria-pressed={value === v}
                className={`px-3 py-1.5 text-sm border transition-colors ${
                  value === v
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-[var(--bg-raised)] hover:bg-[var(--surface)]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        );
      }
      return (
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-32 px-3 py-2 border focus:ring-2 focus:ring-primary-500"
        />
      );

    default:
      return (
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-32 px-3 py-2 border focus:ring-2 focus:ring-primary-500"
        />
      );
  }
}

// ============================================================================
// Main Component
// ============================================================================

export function ValueEntryTask({ task, userId, userName, onComplete }: ValueEntryTaskProps) {
  // State
  const [values, setValues] = useState<Record<string, string | number>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [showNotes, setShowNotes] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<ValueFormat>(
    task.valueEntryConfig?.format || 'fibonacci'
  );
  const [configStep, setConfigStep] = useState<'format' | 'entry'>(
    task.valueEntryConfig ? 'entry' : 'format'
  );
  
  // Get items from candidates store
  const allItems = useItemsStore(state => state.items);
  const items = useMemo(() => {
    return task.itemIds
      .map(id => allItems.find(c => c.id === id))
      .filter((c): c is NonNullable<typeof c> => !!c);
  }, [task.itemIds, allItems]);
  
  // Store actions
  const submitResponse = usePrioritizationTaskStore(state => state.submitResponse);
  
  // Check completion
  const allItemsHaveValues = items.every(item => values[item.id] !== undefined && values[item.id] !== '');
  const completedCount = items.filter(item => values[item.id] !== undefined && values[item.id] !== '').length;
  const progress = items.length > 0 ? (completedCount / items.length) * 100 : 0;
  
  // Check if already submitted
  const existingResponse = task.responses.find(r => r.participantId === userId);
  
  const handleValueChange = (itemId: string, value: string | number) => {
    setValues(prev => ({ ...prev, [itemId]: value }));
  };
  
  const handleNoteChange = (itemId: string, note: string) => {
    setNotes(prev => ({ ...prev, [itemId]: note }));
  };
  
  const handleSubmit = async () => {
    if (!allItemsHaveValues) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const responseData: ValueEntryResponse = {
        type: 'value-entry',
        values,
        notes: Object.keys(notes).length > 0 ? notes : undefined,
      };
      
      // "Record what it actually took": the answer is a realization (loop two,
      // PRD-value-suggestion-agent §6.2), written through the one write path
      // before the task closes. Created by the value_calibration job for the item's owner.
      const recordActual = task.toolConfig as { kind?: string; attribute_key?: string; scale_id?: string | null } | undefined;
      if (recordActual?.kind === 'record_actual' && recordActual.attribute_key) {
        for (const [itemId, value] of Object.entries(values)) {
          const { error } = await recordValueRealization({
            itemId,
            attributeKey: recordActual.attribute_key,
            realizedValue: value,
            source: 'person',
            scaleId: recordActual.scale_id ?? null,
          });
          if (error) throw new Error(error);
        }
      }
      // An ask (migration 1054): each value lands on its item as a proposal, the
      // way a value typed in any session does, and the ask is finished once all
      // are saved. A refusal stops here and is shown.
      const ask = task.toolConfig as { ask?: boolean; attribute_key?: string; scale_id?: string | null } | undefined;
      const isAsk = !!(ask?.ask && ask.attribute_key && task.sessionId);
      if (isAsk) {
        for (const [itemId, value] of Object.entries(values)) {
          const saved = await saveSessionValue({
            itemId,
            attributeKey: ask!.attribute_key!,
            value,
            sessionId: task.sessionId,
            sessionName: task.name,
            userId,
            userName,
            mayAcceptForSession: false,
            scaleId: ask!.scale_id ?? null,
          });
          if (!saved.contributed) throw new Error(saved.error ?? 'Your value was not saved.');
        }
      }
      if (isAsk) {
        // The values are on the items; the task completes with its session (820).
        // Writing the task row here as well would race that and reopen it.
        const finished = await finishEstimateAsk(task.id);
        if (!finished.ok) throw new Error(finished.error);
        usePrioritizationTaskStore.setState((state) => ({
          tasks: state.tasks.map((t) => (t.id === task.id ? { ...t, status: 'completed' as const } : t)),
        }));
      } else {
        submitResponse({
          taskId: task.id,
          participantId: userId,
          participantName: userName,
          data: responseData,
        });
      }
      
      onComplete?.();
    } catch (error) {
      console.error('[ValueEntryTask] Submit error:', error);
      setSubmitError(error instanceof Error ? error.message : 'Your values were not saved.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Show existing response if already submitted
  if (existingResponse) {
    const data = existingResponse.data as ValueEntryResponse;
    return (
      <div className="space-y-6">
        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-4">
          <div className="flex items-center gap-2 text-[var(--green)] dark:text-[var(--text-ter)]">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Response submitted</span>
          </div>
          <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mt-1">
            You submitted your {task.attributeName || 'values'} on {new Date(existingResponse.submittedAt).toLocaleString()}.
          </p>
        </div>
        
        <div className="bg-[var(--bg-raised)] border divide-y">
          <div className="px-4 py-3 bg-[var(--surface)] font-medium text-[var(--text-sec)]">
            Your Values
          </div>
          {items.map(item => (
            <div key={item.id} className="px-4 py-3 flex items-center justify-between">
              <span className="text-[var(--text-pri)]">{item.title}</span>
              <span className="font-medium text-primary-600">
                {data.values[item.id] ?? '-'}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Format selection step
  if (configStep === 'format') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <FileEdit className="h-12 w-12 mx-auto text-[var(--text-ter)] mb-4" />
          <h2 className="text-xl font-semibold text-[var(--text-pri)]">
            How would you like to express {task.attributeName || 'values'}?
          </h2>
          <p className="text-[var(--text-sec)] mt-2">
            Choose the format that works best for your estimates.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {VALUE_FORMATS.map(format => {
            const Icon = format.icon;
            return (
              <button
                key={format.id}
                onClick={() => setSelectedFormat(format.id)}
                className={`p-4  border-2 text-left transition-all ${
                  selectedFormat === format.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2  ${
                    selectedFormat === format.id ? 'bg-primary-100' : 'bg-[var(--hover-tint)]'
                  }`}>
                    <Icon className={`h-5 w-5 ${
                      selectedFormat === format.id ? 'text-primary-600' : 'text-[var(--text-ter)]'
                    }`} />
                  </div>
                  <div>
                    <div className="font-medium text-[var(--text-pri)]">{format.label}</div>
                    <div className="text-sm text-[var(--text-ter)]">{format.description}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        
        <div className="flex justify-end">
          <button
            onClick={() => setConfigStep('entry')}
            className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white hover:bg-primary-700"
          >
            Continue
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }
  
  // Value entry step
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-[var(--text-pri)]">
          Provide {task.attributeName || 'Values'}
        </h2>
        <p className="text-[var(--text-sec)] mt-1">
          Enter your {selectedFormat === 'tshirt' ? 't-shirt size' : 'estimate'} for each item below.
        </p>
      </div>
      
      {/* Progress */}
      <div className="bg-[var(--bg-raised)] border p-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-[var(--text-sec)]">Progress</span>
          <span className="font-medium text-[var(--text-pri)]">{completedCount} / {items.length} items</span>
        </div>
        <div className="h-2 bg-[var(--hover-tint)] rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      
      {/* Items List */}
      <div className="bg-[var(--bg-raised)] border divide-y">
        {items.map((item, index) => (
          <div key={item.id} className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--text-ter)]">#{index + 1}</span>
                  <h3 className="font-medium text-[var(--text-pri)]">{item.title}</h3>
                </div>
                {item.description && (
                  <p className="text-sm text-[var(--text-sec)] mt-1 line-clamp-2">{item.description}</p>
                )}
              </div>
              
              <div className="flex-shrink-0">
                <ValueInput
                  format={selectedFormat}
                  value={values[item.id]}
                  onChange={(v) => handleValueChange(item.id, v)}
                  config={task.valueEntryConfig}
                />
              </div>
            </div>
            
            {/* Notes toggle */}
            <div className="mt-2">
              {showNotes[item.id] ? (
                <textarea
                  value={notes[item.id] || ''}
                  onChange={(e) => handleNoteChange(item.id, e.target.value)}
                  placeholder="Add a note (optional)..."
                  className="w-full px-3 py-2 text-sm border resize-none"
                  rows={2}
                />
              ) : (
                <button
                  onClick={() => setShowNotes(prev => ({ ...prev, [item.id]: true }))}
                  className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                >
                  + Add note
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Submit */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setConfigStep('format')}
          className="text-[var(--text-sec)] hover:text-[var(--text-pri)]"
        >
          Change format
        </button>
        
        <button
          onClick={handleSubmit}
          disabled={!allItemsHaveValues || isSubmitting}
          className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            'Submitting...'
          ) : (
            <>
              <Save className="h-4 w-4" />
              Submit Values
            </>
          )}
        </button>
      </div>
      
      {submitError && (
        <div className="flex items-center gap-2 text-[var(--coral)] text-sm">
          <AlertCircle className="h-4 w-4" />
          {submitError}
        </div>
      )}

      {!allItemsHaveValues && (
        <div className="flex items-center gap-2 text-[var(--mustard)] text-sm">
          <AlertCircle className="h-4 w-4" />
          Please provide values for all {items.length - completedCount} remaining items
        </div>
      )}
    </div>
  );
}

export default ValueEntryTask;
